<template>
  <div class="flex min-h-screen justify-center items-center px-6 py-12 lg:px-8 bg-gradient-to-br from-red-600 to-indigo-600">
    <div class="bg-white p-12 rounded-xl">
    <div class="sm:mx-auto sm:w-full sm:max-w-sm ">
      <a href="https://www.lumbredocs.com.ar" target="_blank" rel="noopener noreferrer">
        <img class="mx-auto lg:h-10 " src="@/assets/images/logo-lumbredocs-black.png" alt="Logo" />
      </a>
      <h2 v-if="!loading" class="mt-9 text-center text-2xl font-bold leading-9 tracking-tight text-gray-800">{{$t('sign_in_your_account')}}</h2>
      <h2 v-else class="mt-2 text-center text-2xl font-bold leading-9 tracking-tight text-white">{{$t('loading')}}</h2>
    </div>

    <div v-if="!loading" class="mt-4 sm:mx-auto sm:w-full sm:max-w-sm">
      <form class="space-y-6" @submit.prevent="onSubmit">
        <div>
          <label for="email" class="block text-sm font-semibold leading-6 text-gray-800">{{$t('email_address')}}</label>
          <div class="mt-2">
            <input
              id="email"
              name="email"
              type="email"
              v-model.lazy="$v.form.email.$model"
              required
              tabindex="1"
              :class="{ 'error': $v.form.email.$error }"
              class="block w-full rounded-md border-gray-800 py-1.5 text-gray-800 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-100 sm:text-sm sm:leading-6 pl-2">
          </div>
          <!-- <p v-if="$v.form.email.$invalid" class="text-white text-xs italic mt-3">Ingrese una dirección de mail.</p> -->
        </div>

        <div>
          <div class="flex items-center justify-between">
            <label v-if="!forgotPasswordMode" for="password" class="block text-sm leading-6 text-gray-800 font-semibold">{{$t('password')}}</label>
            <div class="text-sm">
              <a href="#" class="font-semibold text-indigo-800 hover:text-indigo-600" @click="forgotPasswordMode = !forgotPasswordMode">
                {{ forgotPasswordMode ? $t('try_again') : $t('forgot_password') }}
              </a>
            </div>
          </div>
          <div v-if="!forgotPasswordMode" class="mt-2">
            <div class="position-relative">
            <input
              id="password"
              name="password"
              :type="showInputValue ? 'text' : 'password'"
              v-model.lazy="$v.form.password.$model"
              required
              tabindex="2"
              :class="{ 'error': $v.form.password.$error }"
              class="block w-full rounded-md  py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-100 sm:text-sm sm:leading-6 pl-2">
              <font-awesome-icon
                id="font-awesome-icon-eye"
                v-if="!showInputValue"
                icon="eye"
                size="sm"
                class="clickable eyes"
                @click="showInputValue = true"
                />
              <font-awesome-icon
                id="font-awesome-icon-eye-slash"
                v-if="showInputValue"
                icon="eye-slash"
                size="sm"
                class="clickable eyes"
                @click="showInputValue = false"
                />
              </div>
            </div>
        </div>

        <div>
          <button v-if="!isAuthenticated"
            type="submit"
            :disabled="$v.form.$error"
            class="flex w-full justify-center rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
            :class="{'cursor-not-allowed': $v.form.$error}"
          >
              <template v-if="!loading">
                {{ forgotPasswordMode ? $t('recover_password') : $t('sign_in_btn') }}
              </template>
              <template v-else>
                <i class="mdi mdi-loading lm-button-loading"></i>
              </template>
          </button>
        </div>
      </form>

      <p class="mt-10 text-center text-sm text-indigo-800">
        Not a member?
        <router-link to="/trial" class="font-bold leading-6 text-indigo-800 hover:text-indigo-600">Start a 14 day free trial</router-link>
      </p>
      <a href="https://www.lumile.com.ar" target="_blank" rel="noopener noreferrer">
        <img class="mt-4 mx-auto h-5 w-auto" :src="logoLumileWhite" alt="Logo" style="filter: invert();">
      </a>
    </div>
    <div v-else class="flex justify-center items-center">
      <LmComponentLoading />
    </div>
    </div>
  </div>
</template>

<script>
import LmTextInput from '@/components/form/elements/LmTextInput'
import LmButton from '@/components/form/elements/LmButton'
import LmTreeSelect from '@/components/form/elements/LmTreeSelect'
import LmPassword from '@/components/form/elements/LmPassword'
import store from '@/store'
import { mapState, mapGetters, mapActions } from 'vuex'
import { required, requiredIf, email } from 'vuelidate/lib/validators'
import commonsApi from '@/api/commons'
import lmToasts from '@/mixins/lmToasts.js'

import { generateRoutes } from '@/helpers/LmDynamicRoutes'

export default {
  name: 'Login',
  inject: ['lumbredocs_v3'],
  components: {
    LmTextInput,
    LmPassword,
    LmTreeSelect,
    LmButton
  },
  mixins: [lmToasts],
  data () {
    return {
      showInputValue: false,
      form: {
        email: '',
        customer: null,
        password: null
      },
      serverErrorMessage: '',
      loading: false,
      sentRestoreEmailSuccessfully: false,
      forgotPasswordMode: false,
      enableForgotPassword: process.env?.VUE_APP_ENABLE_FORGOT_PASSWORD === 'yes',
      errorUserOrPassword: false,
      isOnMesaContext: process.env?.VUE_APP_LUMBREDOCS_V3_MESA === 'yes'
    }
  },
  validations: {
    form: {
      email: {
        required: requiredIf(function (val) {
          return this.loginByEmail && val
        }),
        email
      },
      username: {
        required: requiredIf(function (val) {
          return !this.loginByEmail && val
        })
      },
      /* customer: {
        required
      }, */
      password: {
        required
      }
    }
  },
  methods: {
    ...mapActions('notifications', ['getNotifications']),
    onSubmit () {
      this.forgotPasswordMode ? this.onForgotPassword() : this.login()
    },
    async onForgotPassword () {
      this.$v.form.$touch()
      if (!this.$v.form.email.$invalid) {
        this.loading = true
        try {
          await commonsApi.axiosPostWrapper(
            process.env?.VUE_APP_FORGOT_PASSWORD_URL + this.form.email
          )
          this.loading = false
          this.sentRestoreEmailSuccessfully = true
          this.lmToastSuccessMessage('Recibirás un email con los pasos a seguir.', 'Solicitud exitosa')
        } catch (err) {
          // this.lmToastDangerMessage(err.response.data.message, `${err.response.data.type} - ${err.response.data.code}`)
        } finally {
          this.loading = false
        }
      }
    },
    login () {
      if (this.authMode === 'session') {
        this.$v.form.$touch()
        if (!this.$v.form.$pending && !this.$v.form.$error) {
          this.loading = true
          store.dispatch('session/login', this.form)
            .then(async _ => {
              if (process?.env?.VUE_APP_DYNAMIC_ROUTER === 'yes') {
                // Genero las rutas dinamicas para el usuario logueado
                const routes = await generateRoutes()
                // Inserto las rutas en la instancia de vue-router
                routes.forEach(route => {
                  this.$router.addRoute(route)
                })
              }
            })
            .then(_ => {
              // Busco los parámetros de la url para el "redirect"
              const params = new Proxy(new URLSearchParams(location.search), {
                get: (searchParams, prop) => searchParams.get(prop)
              })
              const urlToRedirect = params?.redirect || '/'
              this.$router.push(urlToRedirect)
              if (process.env.VUE_APP_ENABLE_LUMBRE_NOTIFICATIONS === 'yes') {
                this.getNotifications()
              }
            })
            .catch(error => {
              // if (typeof error.response.data.message !== 'undefined') {
              //   this.serverErrorMessage = error.response.data.message
              //   console.log('error.response.data.message', error.response.data.message)
              // }
              console.log(error)
              // this.lmToastDangerMessage('Email o contraseña inválida.', 'Error')
              this.errorUserOrPassword = true
            })
            .finally(_ => {
              this.loading = false
            })
        }
      } else {
        store.dispatch('session/login', {})
      }
    },
    logout () {
      store.dispatch('session/logout')
    },

    // Evito que el background se carge de manera escalonada (fix visual)
    showBgWhenIsLoaded () {
      const src = this.bgImage
      const image = new Image()
      image.addEventListener('load', function () {
        document.querySelector('.login-background').style.backgroundImage = `url(${src})`
      })
      image.src = src
    }
  },
  computed: {
    ...mapGetters('session', ['isAuthenticated']),
    ...mapState('settings', ['appName', 'logoHomeImg', 'title1', 'title2', 'text', 'bgImage', 'loginBgColor', 'loginLogoWidth', 'loginLogoJustifyContent', 'loginTextAlign', 'customerList', 'logoLumileWhite', 'loginH1Text']),
    authMode () {
      return process.env.VUE_APP_AUTH_MODE
    },
    loginByEmail () {
      if (process.env?.VUE_APP_SESSION_LOGIN_BY_EMAIL === 'yes') {
        return true
      } else {
        return false
      }
    }
  },
  created () {
    if (this.isAuthenticated) {
      this.$router.replace('/')
    }
    this.form.customer = JSON.parse(localStorage.getItem('currentCustomer'))

    if (this.lumbredocs_v3) {
      this.showBgWhenIsLoaded()
    }
  },
  mounted () {
    this.$bvToast.hide('lm-toast-success-internal-link')

    // Agrega clase al body
    document.body.classList.add('login')
    document.body.classList.remove('trial')
  }
}
</script>
<style scoped>
.eyes{
    position: absolute;
    top: 12px;
    right: 12px;
    z-index: 1;
  }
#email, #password{
  border: 1px solid rgba(152, 166, 173, 0.5);
}

</style>
